import { FormControl, MenuItem, Select, Typography, Stack } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { FieldDataItem } from "../classes/field_data_item";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { gql, useMutation } from "@apollo/client";

export const availableCultures = [
  "Corn",
  "Barley",
  "Wheat",
  "Soybean",
  "Meadow",
  "Vineyard",
];

export const availablePhenologicalPhases: Record<string, string[]> = {
  "default": [
    "Emergence",
    "Rising",
    "Flowering",
    "Ripening",
  ],
  "Vineyard": [
    "Vegetative",
    "Flowering",
    "Fruit Setting",
    "Veraison/Maturation",
  ],
}

export const getAvailablePhenologicalPhases = (culture: string | null) => {
  if (culture) {
    return availablePhenologicalPhases[culture] ?? availablePhenologicalPhases.default;
  }
  return availablePhenologicalPhases.default;
}

export interface FieldInfoContentProps {
  field?: {
    id: number;
    name: string;
  } | null;
  agricultureAnalysisData?: {
    id: number;
    phase: string;
  };
  fieldDataItems?: FieldDataItem[];
  fieldBoundary?: google.maps.LatLngLiteral[];
  photogrammetry?: boolean;
  displayMode?: 'table' | 'stack';
  showAreaInfo?: boolean;
  showAreaName?: boolean;
}

const FieldInfoContent = (props: FieldInfoContentProps) => {
  const [seedDate, setSeedDate] = React.useState<Dayjs | null>(null);
  const [culture, setCulture] = React.useState<string | null>(null);
  const [phase, setPhase] = React.useState<string | null>(props.agricultureAnalysisData?.phase || null);

  const { t } = useTranslation();

  const [insertSeedDate] = useMutation(gql(`
    mutation InsertSeedDate($field_id: bigint!, $value: String!) {
      insert_field_data_items(objects: {
        field_id: $field_id,
        type: "Seed Date",
        value: $value,
      }) {
        returning {
          id
        }
      }
    }
  `), {
    refetchQueries: ['FieldDetails'],
  });

  const [insertCulture] = useMutation(gql(`
    mutation InsertCulture($field_id: bigint!, $value: String!) {
      insert_field_data_items(objects: {
        field_id: $field_id,
        type: "Culture",
        value: $value,
      }) {
        returning {
          id
        }
      }
    }
  `), {
    refetchQueries: ['FieldDetails'],
  });

  const [updatePhenologicalPhase] = useMutation(gql(`
    mutation UpdatePhenologicalPhase($agriculture_analysis_data_id: bigint!, $phase: String!) {
      update_agriculture_analysis_data_by_pk(pk_columns: {
        id: $agriculture_analysis_data_id,
      }, _set: {
        phase: $phase,
      }) {
        id
      }
    }
  `), {
    refetchQueries: ['FieldDetails'],
  });

  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = React.useState(false);

  // Check if Google Maps API is loaded
  React.useEffect(() => {
    const checkGoogleMaps = () => {
      if ((window as any)?.google?.maps?.geometry?.spherical) {
        setIsGoogleMapsLoaded(true);
      }
    };

    checkGoogleMaps();
    
    // If not loaded yet, check periodically
    if (!isGoogleMapsLoaded) {
      const interval = setInterval(checkGoogleMaps, 100);
      return () => clearInterval(interval);
    }
  }, [isGoogleMapsLoaded]);

  const areaSquareMeters = React.useMemo(() => {
    if (!props.fieldBoundary || !isGoogleMapsLoaded) return 0;
    
    const spherical = (window as any).google.maps.geometry.spherical;
    return spherical.computeArea(
      props.fieldBoundary.map((p: any) => new (window as any).google.maps.LatLng(p.lat, p.lng))
    );
  }, [props.fieldBoundary, isGoogleMapsLoaded]);

  React.useEffect(() => {
    if (props.fieldDataItems) {
      let seedFound = false;
      let cultureFound = false;
      for (const item of props.fieldDataItems) {
        if (!seedFound && item.type === 'Seed Date') {
          setSeedDate(dayjs(item.value));
          seedFound = true;
        } else if (!cultureFound && item.type === 'Culture') {
          setCulture(item.value);
          cultureFound = true;
        }
      }
    }
  }, [props.fieldDataItems]);

  React.useEffect(() => {
    if (props.agricultureAnalysisData) {
      setPhase(props.agricultureAnalysisData.phase);
    }
  }, [props.agricultureAnalysisData]);

  const fieldInfoItems = [
    ...(props.showAreaName ? [
      {
        label: t('Name'),
        value: props.field?.name,
        display: 'text' as const,
      }
    ] : []),
    ...(props.showAreaInfo ? [
      {
        label: t('analysis.area_meters'),
        value: `${areaSquareMeters.toFixed(2)} m²`,
        display: 'text' as const,
      },
      {
        label: t('analysis.area_hectares'),
        value: `${(areaSquareMeters / 10000).toFixed(2)} ha`,
        display: 'text' as const,
      },
    ] : []),
    ...(!props.photogrammetry ? [
      {
        label: t('Seed Date'),
        value: seedDate,
        display: 'datepicker' as const,
        onChange: async (newValue: Dayjs | null) => {
          const date = newValue?.toISOString();
          if (!date || date === seedDate?.toISOString()) {
            return;
          }

          await insertSeedDate({
            variables: {
              field_id: props.field!.id,
              value: date,
            },
          });
          setSeedDate(newValue);
        },
      },
      {
        label: t('Culture'),
        value: culture,
        display: 'select' as const,
        options: availableCultures,
        onChange: async (value: string) => {
          if (!value || value === culture) {
            return;
          }

          await insertCulture({
            variables: {
              field_id: props.field!.id,
              value: value,
            },
          });
          setCulture(value);
        },
      },
      ...(props.agricultureAnalysisData ? [
        {
          label: t('Phenological Phase'),
          value: phase,
          display: 'select' as const,
          options: getAvailablePhenologicalPhases(culture),
          onChange: async (value: string) => {
            if (value === phase) {
              return;
            }

            await updatePhenologicalPhase({
              variables: {
                agriculture_analysis_data_id: props.agricultureAnalysisData?.id,
                phase: value,
              },
            });
            setPhase(value);
          },
        },
      ] : []),
    ] : []),
  ].filter(item => item.value !== undefined);

  const renderFieldItem = (item: any) => {
    switch (item.display) {
      case 'text':
        return <Typography variant="body2">{item.value}</Typography>;
      
      case 'datepicker':
        return (
          <DatePicker
            sx={{ width: '100%' }}
            value={item.value}
            onChange={item.onChange}
          />
        );
      
      case 'select':
        return (
          <FormControl fullWidth>
            <Select
              value={item.value ?? ''}
              onChange={(event) => item.onChange?.(event.target.value)}
            >
              <MenuItem value={''}><em>{t('None')}</em></MenuItem>
              {item.options?.map((option: string) => (
                <MenuItem key={option} value={option}>{t(option)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      
      default:
        return null;
    }
  };

  if (props.displayMode === 'stack') {
    return (
      <Stack direction="column" spacing={1}>
        {fieldInfoItems.map((item, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ minWidth: 100, fontWeight: 'bold' }}>
              {item.label}:
            </Typography>
            {renderFieldItem(item)}
          </Stack>
        ))}
      </Stack>
    );
  }

  // Default table display mode
  return (
    <table style={{
      width: '100%',
      borderCollapse: 'collapse',
    }}>
      <tbody>
        {fieldInfoItems.map((item, index) => (
          <tr key={index}>
            <th style={{
              textAlign: 'right',
              padding: '8px',
            }}>
              {item.label}
            </th>
            <td style={{
              padding: '8px',
            }}>
              {renderFieldItem(item)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default FieldInfoContent;
